import { observer } from "mobx-react";
import {
  extractPortfolioContactsAdminUrl,
  PortfolioSignaturesReadyState,
  PortfolioSignaturesTab,
  SelectedPortfolioOption,
} from "../portfolioDomain";
import { useLanguage, useTranslation } from "../../stores/RootStoreContext";
import { Box, Container, VBox } from "../../components-v2/Box";
import { ViewDocumentSigningStatusDialog } from "../../dbot/summary/summaryComponents";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { DocumentSigningStatus } from "../../document/documentApi";
import { Tag, TagColor } from "../../components-v2/Tag";
import { TFunction, TMessage } from "../../i18n/tt";
import { Language } from "../../i18n/languages";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";
import { compact } from "lodash";
import { isMobileView } from "../../utils/screen";
import { MTextInput } from "../../components-v2/TextInput";
import { MSelect } from "../../components-v2/Select";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { signDocumentLabel, signingMethodLabel } from "../../common/common";
import { IconName } from "../../components-v2/iconMapping";
import { Icon } from "../../components-v2/Icon";
import { stopPropagation } from "../../utils/events";
import {
  canSendForSigning,
  getDocumentPreview,
  getTitle,
  groupBySigningMethod,
  prepareCompaniesForSigning,
  prepareDocumentsForSigning,
  PreparedPortfolioSignaturesState,
  SendForSigningState,
  SentForSigningLinkAction,
  SignatureActions,
  SignatureItem,
} from "./signaturesDomain";
import { EmptyState } from "../../components-v2/EmptyState";
import AutomatedSignatures from "../../assets/img/automated-signatures-general.svg";
import { ReactElement } from "react";
import {
  ConfirmDissolveDialog,
  InvalidPortfolioView,
  PortfolioSettingsDialog,
} from "../portfolioComponents";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { isPreparedPageState } from "../../signing/signDocumentDomain";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";
import { AppBarData } from "../../appBar/appBarDomain";
import { Tabs } from "../../components-v2/Tabs";
import { FullScreenLayout } from "../../components-v2/FullScreenLayout";
import { BatchPreviewDialog } from "../../components-v2/BatchPreviewDialog";
import { Dialog } from "../../components-v2/Dialog";
import { UploadFilesPanel } from "../../components-v2/UploadFilesPanel";
import { Table } from "../../components-v2/Table";
import { Autocomplete } from "../../components-v2/Autocomplete";
import { SelectFilesButton } from "../../components-v2/SelectFilesButton";
import { SendForSigningDialog } from "../../common/commonComponents";
import { getEntryLabel } from "../../archive/archiveDomain";
import { relativeTime } from "../../i18n/dateFormat";
import { toDate } from "../../utils/Dates";
import { Markdown } from "../../text/Markdown";
import { Alert } from "../../components-v2/Alert";
import { AppBar } from "../../components-v2/AppBar";
import { FollowUpLeftPanel } from "../followUp/followUpComponents";

export const EmptySignaturesView = observer(
  ({ title, description }: { title: string; description: string }) => (
    <VBox grow={"golden"} justifyContent={"center"}>
      <EmptyState
        illustration={<img src={AutomatedSignatures} alt="dCompany" />}
        title={title}
        description={description}
      />
    </VBox>
  ),
);

const SignaturesTabs = observer(
  ({ state, actions }: { state: PortfolioSignaturesReadyState; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const render = (tab: PortfolioSignaturesTab | undefined) => {
      switch (tab) {
        case "signed":
          return <Signed state={state} actions={actions} />;
        case "sent":
          return <Sent state={state} actions={actions} />;
        case "sealed":
          return <Sealed state={state} actions={actions} />;
        default:
          return <Pending state={state} actions={actions} />;
      }
    };
    return (
      <Container padding maxWidth="xl">
        {state.documentPreview && (
          <BatchPreviewDialog
            model={state.documentPreview}
            onClose={actions.closeDocumentPreview}
          />
        )}
        <Box display={"block"}>
          {state.displayGlobalSignatureEmptyState ? (
            <VBox>
              <EmptySignaturesView
                title={t("signature.empty-state-title-global")}
                description={t("signature.empty-state-description-global")}
              />
              <Box justifyContent={"center"} marginTop={"lg"}>
                {!isMobileView() && state.signaturesResponse.hasAdminRole && (
                  <SendForSigningDocumentButton state={state} actions={actions} />
                )}
              </Box>
            </VBox>
          ) : (
            render(state.signaturesTab)
          )}
        </Box>
        {state.signingStatusDialogModel && (
          <ViewDocumentSigningStatusDialog model={state.signingStatusDialogModel} />
        )}
      </Container>
    );
  },
);

const Pending = observer(
  ({ state, actions }: { state: PortfolioSignaturesReadyState; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const signatureItems = actions.filterSignatures(locale);

    return (
      <VBox>
        {state.signaturesResponse.signatures?.pending.length === 0 ? (
          <EmptySignaturesView
            title={t("signature.empty-state-title-pending")}
            description={t("signature.empty-state-description-pending")}
          />
        ) : (
          <>
            <Container maxWidth="xl">
              <Box
                justifyContent="space-between"
                alignItems="center"
                marginBottom
                vertical={isMobileView()}
              >
                <SearchAndToolBarSignatures state={state} />
                <ReviewAndSignAll state={state} actions={actions} />
              </Box>
              <DataTable<SignatureItem>
                onRowClick={(signature) => actions.openDocument(signature, "pending")}
                tableData={signatureItems}
                tableColumns={[
                  titleColumn(t, "file-text", undefined, { key: "portfolio.signatures.to-sign" }),
                  signingStatusWithLinkColumn(t, actions),
                  signingMethodColumn(t),
                  requestedByColumn(t),
                ]}
                getId={(row) => row.documentReference}
                initialSortBy={"sentAt"}
                initialSortDirection={"desc"}
              />
            </Container>
          </>
        )}
        <Container alignItems={"center"} marginTop={"lg"}>
          {state.documentSignaturesMessage && (
            <Markdown value={t(state.documentSignaturesMessage)} />
          )}
        </Container>
      </VBox>
    );
  },
);

const Signed = observer(
  ({ state, actions }: { state: PortfolioSignaturesReadyState; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const locale = useLanguage();

    const signatureItems = actions.filterSignatures(locale);
    return (
      <>
        {state.signaturesResponse.signatures?.signed.length === 0 ? (
          <EmptySignaturesView
            title={t("signature.empty-state-title-signed")}
            description={t("signature.empty-state-description-signed")}
          />
        ) : (
          <>
            <Container maxWidth="xl">
              <SearchAndToolBarSignatures state={state} />
              <DataTable<SignatureItem>
                inline
                onRowClick={(signature) => actions.openDocument(signature, "signed")}
                tableData={signatureItems}
                tableColumns={[
                  titleColumn(t, "seal-check"),
                  {
                    name: "tag",
                    label: "",
                    align: "left",
                    getValue: ({ signingStatus }) => {
                      const [label, color] =
                        signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
                          ? [t("archive.signed"), "success"]
                          : [t("portfolio.signatures.to-sign"), "info"];
                      return <Tag label={label} color={color as TagColor} />;
                    },
                  },
                  {
                    name: "signingStatus",
                    label: t("status"),
                    align: "left",
                    getValue: (signature) => (
                      <VBox gap={false} padding={false} alignItems="start">
                        <SigningStatus signature={signature} actions={actions} />
                        {signature.signingStatus !== DocumentSigningStatus.SIGNING_COMPLETE && (
                          <div className={"subtle text-xs"}>
                            {t("portfolio.signatures.you-signed")}
                          </div>
                        )}
                      </VBox>
                    ),
                    getSortValue: (signature) =>
                      signature.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
                        ? t("portfolio.signatures.you-signed")
                        : "",
                  },
                  signedAtColumn(t, locale),
                  requestedByColumn(t),
                ]}
                getId={(row) => row.documentReference}
                initialSortBy={"signedAt"}
                initialSortDirection={"desc"}
              />
            </Container>
          </>
        )}
      </>
    );
  },
);

const Sent = observer(
  ({ state, actions }: { state: PortfolioSignaturesReadyState; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const locale = useLanguage();

    return (
      <>
        {state.signaturesResponse.signatures?.sent.length === 0 ? (
          <EmptySignaturesView
            title={t("signature.empty-state-title-sent")}
            description={t("signature.empty-state-description-sent")}
          />
        ) : (
          <>
            <Container maxWidth="xl">
              <SearchAndToolBarSignatures state={state} />
              <DataTable<SignatureItem>
                inline
                onRowClick={(signature) => actions.openDocument(signature, "sent")}
                tableData={actions.filterSignatures(locale)}
                tableColumns={[
                  titleColumn(t, "file-text"),
                  {
                    name: "tag",
                    label: "",
                    align: "left",
                    getValue: () => <Tag label={t("sent-for-signing")} color={"warning"} />,
                  },
                  signingStatusWithLinkColumn(t, actions),
                  requestedByColumn(t),
                  documentLocationColumn(t, actions),
                ]}
                getId={(row) => row.documentReference}
                initialSortBy={"sentAt"}
                initialSortDirection={"desc"}
              />
            </Container>
          </>
        )}
      </>
    );
  },
);

const Sealed = observer(
  ({ state, actions }: { state: PortfolioSignaturesReadyState; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const locale = useLanguage();

    return (
      <>
        {state.signaturesResponse.signatures?.sealed.length === 0 ? (
          <EmptySignaturesView
            title={t("signature.empty-state-title-sealed")}
            description={t("signature.empty-state-description-sealed")}
          />
        ) : (
          <>
            <Container maxWidth="xl">
              <SearchAndToolBarSignatures state={state} />
              <DataTable<SignatureItem>
                inline
                onRowClick={(signature) => actions.openDocument(signature, "sealed")}
                tableData={actions.filterSignatures(locale)}
                tableColumns={[
                  titleColumn(t, "seal-check", true),
                  {
                    name: "tag",
                    label: "",
                    align: "left",
                    getValue: () => <Tag label={t("archive.signed")} color={"success"} />,
                  },
                  signingStatusWithLinkColumn(t, actions),
                  signedAtColumn(t, locale),
                  documentLocationColumn(t, actions),
                ]}
                getId={(row) => row.documentReference}
                initialSortBy={"signedAt"}
                initialSortDirection={"desc"}
              />
            </Container>
          </>
        )}
      </>
    );
  },
);

const ReviewAndSignAll = observer(
  ({ state, actions }: { state: PortfolioSignaturesReadyState; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const filteredSignDocumentCount = state.signaturesResponse?.signatures.pending;
    if (filteredSignDocumentCount && filteredSignDocumentCount.length > 0) {
      if (filteredSignDocumentCount.length === 1) {
        return (
          <Button
            variant={"primary"}
            label={t("portfolio.signatures.review-and-signall")}
            icon="pen"
            onClick={() => actions.openBatchSigningPage(filteredSignDocumentCount[0].signingMethod)}
          />
        );
      } else {
        return (
          <DropdownMenu
            button={{
              type: "button",
              variant: "primary",
              icon: "pen",
              label: { key: "portfolio.signatures.review-and-signall" },
            }}
            items={groupBySigningMethod(filteredSignDocumentCount).flatMap(
              ({ signingMethod, count }) => {
                const label = signingMethodLabel(signingMethod)!;
                return [
                  {
                    type: "label",
                    label: <Box whitespace={"nowrap"}>{t(label)}</Box>,
                  },
                  {
                    type: "item",
                    label: <Box whitespace={"nowrap"}>{t(signDocumentLabel(count))}</Box>,
                    icon: "pen",
                    onSelect: () => actions.openBatchSigningPage(signingMethod),
                  },
                ];
              },
            )}
          />
        );
      }
    } else {
      return null;
    }
  },
);

function titleColumn(
  t: TFunction,
  icon: IconName,
  weightFill?: boolean,
  infoMessage?: TMessage,
): TableColumn<SignatureItem> {
  return {
    name: "title",
    label: t("document"),
    align: "left",
    getValue: ({ title, company }) => (
      <Title
        icon={icon}
        title={title}
        companyName={company.companyName}
        infoMessage={infoMessage}
        weightFill={weightFill}
      />
    ),
    getSortValue: ({ title, company }) => `${title} ${company.companyName}`,
  };
}

function signingStatusWithLinkColumn(
  t: TFunction,
  actions: SignatureActions,
): TableColumn<SignatureItem> {
  return {
    name: "numberOfPartiesSigned", // to enable sorting.
    label: t("status"),
    align: "left",
    getValue: (signature) => <SigningStatus signature={signature} actions={actions} />,
    getSortValue: ({ numberOfPartiesSigned }) => numberOfPartiesSigned,
  };
}

function signingMethodColumn(t: TFunction): TableColumn<SignatureItem> {
  return {
    name: "signingMethod",
    label: t("signing.signingMethod"),
    align: "left",
    getValue: ({ signingMethod }) =>
      t(signingMethodLabel(signingMethod) ?? "dbot.summary.signingStatus.unknown"),
    getSortValue: ({ signingMethod }) =>
      t(signingMethodLabel(signingMethod) ?? "dbot.summary.signingStatus.unknown"),
  };
}

function requestedByColumn(t: TFunction): TableColumn<SignatureItem> {
  return {
    name: "sentAt",
    label: t("portfolio.signatures.requested"),
    align: "left",
    getValue: ({ requestedBy, sentAt }) => (
      <RequestedBy requestedBy={requestedBy} sentAt={sentAt} />
    ),
    getSortValue: ({ sentAt }) => toDate(sentAt ?? "").getTime(),
  };
}

const RequestedBy = observer(
  ({ requestedBy, sentAt }: { requestedBy: string; sentAt?: string }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <>
        {sentAt && t(relativeTime(sentAt, locale))}{" "}
        {requestedBy === "self" ? t("by_you") : t("by", { name: requestedBy })}
      </>
    );
  },
);

const Title = observer(
  ({
    title,
    companyName,
    infoMessage,
    icon,
    weightFill,
  }: {
    title: string;
    icon: IconName;
    companyName: string;
    infoMessage?: TMessage;
    weightFill?: boolean;
  }) => {
    return (
      <Box justifyContent={"space-between"} alignItems="center">
        <Box alignItems={"center"}>
          <Icon
            name={icon}
            weight={weightFill ? "fill" : undefined}
            color={weightFill ? "success" : undefined}
          />
          <VBox gap={false} padding={false}>
            <div>{title}</div>
            <div className={"subtle text-xs"}>{companyName}</div>
          </VBox>
        </Box>
        {infoMessage && <Tag color={"info"} label={infoMessage} />}
      </Box>
    );
  },
);

const SigningStatus = observer(
  ({ signature, actions }: { signature: SignatureItem; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const label = t("n-of-m-have-signed", {
      n: signature.numberOfPartiesSigned,
      m: signature.totalNumberOfPartiesSentTo,
    });
    return signature.hasAdmin ? (
      <LinkButton
        size="sm"
        weight={"normal"}
        label={label}
        onClick={stopPropagation(() => actions.getSigningStatus(signature))}
      />
    ) : (
      label
    );
  },
);

const SearchAndToolBarSignatures = observer(
  ({ state }: { state: PortfolioSignaturesReadyState }) => {
    const { t } = useTranslation();
    return (
      <Box justifyContent="space-between" marginBottom vertical={isMobileView()}>
        <Box>
          <MTextInput
            placeholder={t("search")}
            icon={"magnifying-glass"}
            model={state.signatureSearchModel}
          />
          {state.contextCompany === undefined && <MSelect model={state.companyFilter} />}
        </Box>
      </Box>
    );
  },
);

function documentLocationColumn(
  t: TFunction,
  actions: SignatureActions,
): TableColumn<SignatureItem> {
  return {
    name: "link",
    label: t("portfolio.signatures.document-location"),
    align: "left",
    getValue: (signature) => (
      <LinkToDocument link={actions.mapToSentForSigningLinkAction(signature)} />
    ),
    notSorted: true,
    stopClick: true,
  };
}

function signedAtColumn(t: TFunction, locale: Language): TableColumn<SignatureItem> {
  return {
    name: "signedAt",
    label: t("portfolio.signatures.completed"),
    align: "left",
    getValue: ({ signedAt }) => signedAt && t(relativeTime(signedAt, locale)),
    getSortValue: ({ signedAt }) => toDate(signedAt ?? "").getTime(),
  };
}

const LinkToDocument = observer(({ link }: { link: SentForSigningLinkAction }) => {
  const { t } = useTranslation();
  return (
    <Box alignItems="center">
      {link && (
        <LinkButton
          size="sm"
          weight="normal"
          icon={link.icon}
          label={compact([link.label, link.title])
            .map((i) => t(i))
            .join(" / ")}
          onClick={link.onClick}
        />
      )}
    </Box>
  );
});

export const SignaturesView = observer(function SignaturesView({
  state,
  alertStore,
  appBar,
  showSigningCompleteDialog,
  closeSigningCompleteDialog,
  companyName,
  portfolio,
  showAdminLinks,
  contextCompany,
}: {
  state: PreparedPortfolioSignaturesState;
  alertStore: AlertMessageStore;
  appBar: AppBarData | null;
  showSigningCompleteDialog: boolean;
  showAdminLinks: boolean;
  closeSigningCompleteDialog: () => void;
  companyName: string;
  portfolio?: SelectedPortfolioOption;
  contextCompany?: string;
}) {
  const renderBody = () => {
    switch (true) {
      case isPreparedPageState(state, "invalid-portfolioid"):
        return <InvalidPortfolioView />;
      case isPreparedPageState(state, "loading-portfolio-signatures"):
        return <LoadingIndicator fullscreen />;
      case isPreparedPageState(state, "portfolio-signatures-ready"):
        return (
          <>
            {portfolio && !state.state.displayGlobalSignatureEmptyState ? (
              <SignaturesHeader
                state={state.state}
                actions={state.actions}
                showSigningCompleteDialog={showSigningCompleteDialog}
                closeSigningCompleteDialog={closeSigningCompleteDialog}
                companyName={companyName}
              />
            ) : undefined}
            <SignaturesTabs {...state} />
            {state.state.confirmDissolvePortfolio && (
              <ConfirmDissolveDialog
                {...state}
                contactsAdminsUrl={extractPortfolioContactsAdminUrl(state.state.portfolio)}
              />
            )}
            {!state.state.confirmDissolvePortfolio && state.state.isPortfolioSettingsOpen && (
              <PortfolioSettingsDialog
                {...state}
                contactsAdminsUrl={extractPortfolioContactsAdminUrl(state.state.portfolio)}
              />
            )}
          </>
        );
    }
  };

  const renderAppBar = () => {
    const subheaderElement: ReactElement | undefined =
      isPreparedPageState(state, "portfolio-signatures-ready") &&
      !state.state.displayGlobalSignatureEmptyState ? (
        <SignaturesHeader
          state={state.state}
          actions={state.actions}
          showSigningCompleteDialog={showSigningCompleteDialog}
          closeSigningCompleteDialog={closeSigningCompleteDialog}
          companyName={companyName}
        />
      ) : undefined;

    return <AppBar data={appBar} disableDropShadow subHeaderElement={subheaderElement} />;
  };

  return (
    <FullScreenLayout
      alertStore={alertStore}
      appBar={portfolio ? appBar : renderAppBar()}
      centerPanelContainerProps={{ htmlProps: { style: { flex: "1 1 auto" } } }}
      leftPanelContainerProps={{ htmlProps: { style: { flex: "0 0 300px" } } }}
      leftPanel={
        portfolio && portfolio.type !== "with-board-role" && portfolio.type !== "my-investments" ? (
          <FollowUpLeftPanel
            portfolio={portfolio}
            selectedTab={"signatures"}
            showAdminLinks={showAdminLinks}
            signDocumentCount={
              isPreparedPageState(state, "portfolio-signatures-ready")
                ? state.state.signDocumentCount
                : undefined
            }
            followUpMattersCount={
              isPreparedPageState(state, "portfolio-signatures-ready")
                ? state.state.followUpMattersCount
                : undefined
            }
            companyInfoCount={
              isPreparedPageState(state, "portfolio-signatures-ready")
                ? state.state.companyInfoCount
                : undefined
            }
            followUpShareholdersCount={
              isPreparedPageState(state, "portfolio-signatures-ready")
                ? state.state.followUpShareholdersCount
                : undefined
            }
            contextCompany={contextCompany}
          />
        ) : undefined
      }
      centerPanel={renderBody()}
      disableAppBarShadow
    />
  );
});

const SignaturesHeader = observer(function SignaturesHeader({
  showSigningCompleteDialog,
  closeSigningCompleteDialog,
  companyName,
  state,
  actions,
}: {
  showSigningCompleteDialog: boolean;
  closeSigningCompleteDialog: () => void;
  companyName: string;
  state: PortfolioSignaturesReadyState;
  actions: SignatureActions;
}) {
  const { t } = useTranslation();

  return (
    <Box bg>
      {
        <SigningCompleteDialog
          showSigningCompleteDialog={showSigningCompleteDialog}
          closeSigningCompleteDialog={closeSigningCompleteDialog}
          companyName={companyName}
        />
      }
      <VBox maxWidth="xl" container marginTop="lg">
        <Box justifyContent={"space-between"} alignItems={"start"}>
          <h4>{t("header.signatures")}</h4>
          {!isMobileView() && state.signaturesResponse.hasAdminRole && (
            <SendForSigningDocumentButton state={state} actions={actions} />
          )}
        </Box>
        <div>
          <Tabs tabs={state.signaturesTabs} />
        </div>
      </VBox>
    </Box>
  );
});

const SendForSigningDocumentButton = observer(
  ({ state, actions }: { state: PortfolioSignaturesReadyState; actions: SignatureActions }) => {
    const { t } = useTranslation();
    const form = state.sendForSigningState?.signersForm;
    const locale = useLanguage();

    const renderDialog = () => {
      switch (state.sendForSigningState?.step) {
        case "select-documents":
          return (
            <Dialog
              title={t("documents.send-documents-for-signing")}
              cancelLabel={t("cancel")}
              submitLabel={t("next")}
              onCancel={actions.closeForSigningDialog}
              submitIcon={"arrow-right"}
              onSubmit={actions.openSendDocumentsForSigningDialog}
              isSubmitDisabled={!canSendForSigning(state.sendForSigningState)}
              fullscreen={isMobileView()}
            >
              <VBox gap={"md"}>
                {state.duplicateFileUploading && (
                  <Alert
                    type={"warning"}
                    message={t(state.duplicateFileUploading)}
                    onClose={actions.closeDuplicateAlertInfo}
                  />
                )}
                {state.sendForSigningState.sendDocumentForSigningItems.length > 0 ? (
                  <UploadDocumentsForSigning
                    state={state.sendForSigningState}
                    actions={actions}
                    contextCompany={state.contextCompany}
                  />
                ) : (
                  <UploadFilesPanel
                    fileUploadConfig={state.sendForSigningState.fileUploadConfig}
                    onUpload={actions.selectDocumentsForSigning}
                  />
                )}
                {state.sendForSigningState.message && (
                  <Alert {...state.sendForSigningState.message} />
                )}
              </VBox>
            </Dialog>
          );
        case "send-documents":
          if (form !== undefined) {
            return (
              <SendForSigningDialog
                form={form}
                title={getTitle(state.sendForSigningState.sendDocumentForSigningItems)}
                canClose={!state.sendForSigningState.isSubmitting}
                canSubmit={form.isValid}
                close={actions.closeForSigningDialog}
                submit={actions.submitDocumentsForSigning}
                addSigner={form.addSigner}
                removeSigner={form.removeSigner}
                canRemoveSigner={(signer) => form.items.length > 1 || !signer.isEmpty}
                quickAddItems={form.quickAddSignerItems}
                isSending={state.sendForSigningState.isSubmitting}
                commonSigningProperties={form.commonSigningProperties(locale)}
                companiesForSigning={prepareCompaniesForSigning(state.sendForSigningState, actions)}
                documentsForSigning={prepareDocumentsForSigning(state.sendForSigningState, actions)}
                preview={getDocumentPreview(state.sendForSigningState)}
                hasSuccessUploadedDocuments
                showMessageFields
                enableFullscreenMode
              />
            );
          } else {
            return null;
          }
        default:
          return null;
      }
    };
    return (
      <>
        <Box>
          <Button
            variant={"secondary"}
            label={t("documents.send-documents-for-signing")}
            icon={"paper-plane"}
            onClick={actions.openSendForSigningDialog}
          />
        </Box>
        {renderDialog()}
      </>
    );
  },
);

export const SigningCompleteDialog = observer(
  ({
    showSigningCompleteDialog,
    closeSigningCompleteDialog,
    companyName,
  }: {
    showSigningCompleteDialog: boolean;
    closeSigningCompleteDialog: () => void;
    companyName: string;
  }) => {
    const { t } = useTranslation();
    return showSigningCompleteDialog ? (
      <Dialog title={companyName} cancelLabel={t("close")} onCancel={closeSigningCompleteDialog}>
        <Box alignItems={"center"}>
          <EmptyState
            illustration={<Icon name={"check-circle"} size={"mega"} color={"success"} />}
            title={t("signing.documentSigned")}
            description={
              <>
                <div>{t("signing.documentSigned.description")}</div>
                <div>{t("signing.documentSigned.completed")}</div>
              </>
            }
          />
        </Box>
      </Dialog>
    ) : null;
  },
);

const UploadDocumentsForSigning = observer(
  ({
    state,
    actions,
    contextCompany,
  }: {
    state: SendForSigningState;
    actions: SignatureActions;
    contextCompany?: string;
  }) => {
    const { t } = useTranslation();
    const notInContextCompany = contextCompany === undefined;
    return (
      <VBox container maxWidth={"xl"}>
        <Table variant={"IN_CARD"}>
          <thead>
            <tr>
              <th align={"left"}>{t("dbot.documents")}</th>
              {notInContextCompany && <th align={"left"}>{t("company")}</th>}
              <th align={"left"}>
                {t("signatures.send-documents-for-signing.destination-folder")}
              </th>
              <th align={"left"}></th>
            </tr>
          </thead>
          <tbody>
            {state.sendDocumentForSigningItems.map((s) => (
              <tr key={s.file.name}>
                <td>
                  <Box alignItems={"center"}>
                    <Icon name={"file-text"} />
                    <div>{s.file.name}</div>
                  </Box>
                </td>
                {notInContextCompany && (
                  <td>
                    <Autocomplete
                      value={s.companyItem}
                      items={state.companiesInPortfolio}
                      onSelect={(item) => actions.setCompanyItemDestination(s.file, item)}
                      idFn={(item) => item.organisationNumber}
                      labelFn={(item) => item.companyName}
                    />
                  </td>
                )}
                <td>
                  <Autocomplete
                    items={s.destinationFolderCandidates.toSorted((a, b) =>
                      t(getEntryLabel(a)).localeCompare(t(getEntryLabel(b))),
                    )}
                    value={s.destination}
                    onSelect={(item) => actions.onSelectDestinationFolder(s, item)}
                    idFn={(item) => item.id}
                    labelFn={(item) => t(getEntryLabel(item))}
                  />
                </td>
                <td>
                  <IconButton
                    icon={"trash"}
                    color={"error"}
                    tooltip={t("remove")}
                    onClick={() => actions.removeDocumentForSigning(s.file)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Box>
          <SelectFilesButton
            variant={"link"}
            icon={"plus"}
            fileUploadConfig={state.fileUploadConfig}
            onChange={(files) => actions.selectDocumentsForSigning(files)}
            label={t("signatures.send-documents-for-signing.add-PDF")}
          />
        </Box>
      </VBox>
    );
  },
);
